import React from "react";

import { AlertTriangle, Edit, PlusCircle, Warehouse } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { PICKUP_ADDRESS_ID, WAREHOUSE_ADDRESS } from "~/constants/warehouse";
import { useToast } from "~/hooks/use-toast";
import { getLocationNames, useLocationState } from "~/hooks/useLocationState";
import AddressDialog from "~/pages/clients/components/client-detail/address/AddressDialog";
import { AddressFormValues } from "~/pages/clients/components/client-detail/address/form/AddressForm";
import {
  addClientAddress,
  updateClientAddress,
} from "~/services/addressService";
import { Address } from "~/types/client";

interface AddressSelectorProps {
  addresses: Address[];
  selectedAddressId: string | null;
  onSelect: (addressId: string) => void;
  isLoading: boolean;
  clientId: string;
  fetchAddresses?: () => Promise<void>;
}

// Function to check if an address is complete (has all required fields)
const isAddressComplete = (address: Address): boolean => {
  return !!(
    address.street &&
    address.number &&
    address.city &&
    address.state &&
    address.comuna
  );
};

// Function to get missing address fields for display
const getMissingAddressFields = (address: Address): string[] => {
  const missing: string[] = [];
  if (!address.street) missing.push("calle");
  if (!address.number) missing.push("número");
  if (!address.city) missing.push("ciudad");
  if (!address.state) missing.push("estado/región");
  if (!address.comuna) missing.push("comuna");
  return missing;
};

const AddressSelector: React.FC<AddressSelectorProps> = ({
  addresses,
  selectedAddressId,
  onSelect,
  isLoading,
  clientId,
  fetchAddresses,
}) => {
  const [isAddressDialogOpen, setIsAddressDialogOpen] = React.useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [editingAddress, setEditingAddress] = React.useState<Address | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { toast } = useToast();

  // Use location state for region/comuna selection
  const {
    selectedRegion,
    setSelectedRegion,
    selectedComuna,
    setSelectedComuna,
    availableComunas,
  } = useLocationState();

  // Real implementation of address submission
  const handleAddAddress = async (
    values: AddressFormValues & { regionId?: string; comunaId?: string }
  ) => {
    if (!clientId) return;

    setIsSubmitting(true);
    try {
      // Get region and comuna names from IDs (use form values if available, fallback to state)
      const regionIdToUse = values.regionId || selectedRegion;
      const comunaIdToUse = values.comunaId || selectedComuna;

      const { regionName, comunaName } = getLocationNames(
        regionIdToUse,
        comunaIdToUse
      );

      // Add the address to the database
      const success = await addClientAddress(
        clientId,
        values,
        regionName,
        comunaName
      );

      if (success) {
        toast({
          title: "Dirección agregada",
          description: "La dirección ha sido agregada exitosamente",
        });

        // Reset form and selection state
        setSelectedRegion("");
        setSelectedComuna("");
        setIsAddressDialogOpen(false);

        // Refresh addresses list
        if (fetchAddresses) {
          await fetchAddresses();
        } else {
          // Fallback to page reload if fetchAddresses is not provided
          window.location.reload();
        }
      }
    } catch (error) {
      console.error("Error adding address:", error);
      toast({
        title: "Error",
        description: "No se pudo agregar la dirección",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle editing an existing address
  const handleEditAddress = async (
    values: AddressFormValues & { regionId?: string; comunaId?: string }
  ) => {
    if (!editingAddress || !clientId) return;

    setIsSubmitting(true);
    try {
      // Get region and comuna names from IDs
      const regionIdToUse = values.regionId || selectedRegion;
      const comunaIdToUse = values.comunaId || selectedComuna;

      const { regionName, comunaName } = getLocationNames(
        regionIdToUse,
        comunaIdToUse
      );

      // Update the address
      const success = await updateClientAddress(
        clientId,
        editingAddress.id,
        values,
        regionName,
        comunaName
      );

      if (success) {
        toast({
          title: "Dirección actualizada",
          description: "La dirección ha sido actualizada exitosamente",
        });

        // Reset form and selection state
        setSelectedRegion("");
        setSelectedComuna("");
        setIsEditDialogOpen(false);
        setEditingAddress(null);

        // Refresh addresses list
        if (fetchAddresses) {
          await fetchAddresses();
        } else {
          // Fallback to page reload if fetchAddresses is not provided
          window.location.reload();
        }
      }
    } catch (error) {
      console.error("Error updating address:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la dirección",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle clicking the edit button
  const handleEditClick = (address: Address, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent address selection
    setEditingAddress(address);

    // Reset location state for editing
    setSelectedRegion("");
    setSelectedComuna("");

    setIsEditDialogOpen(true);
  };

  if (isLoading) {
    return <Skeleton className="h-10 w-full" />;
  }

  return (
    <div className="space-y-3">
      {/* Pickup Option */}
      <div
        className={`cursor-pointer rounded-lg border p-3 transition-colors ${
          selectedAddressId === PICKUP_ADDRESS_ID
            ? "border-primary bg-primary/10"
            : "hover:bg-gray-50"
        } border-l-4 border-l-blue-500`}
        onClick={() => onSelect(PICKUP_ADDRESS_ID)}
      >
        <div className="flex items-start gap-3">
          <Warehouse className="mt-0.5 h-4 w-4 text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">
              Cliente retira en bodega
              <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                Pickup
              </span>
            </p>
            <p className="text-xs text-gray-600">
              {WAREHOUSE_ADDRESS.name} - {WAREHOUSE_ADDRESS.street}
            </p>
            <p className="text-xs text-gray-500">
              {WAREHOUSE_ADDRESS.comuna}, {WAREHOUSE_ADDRESS.region},{" "}
              {WAREHOUSE_ADDRESS.country}
            </p>
          </div>
        </div>
      </div>

      {/* Regular Addresses */}
      {addresses.length === 0 ? (
        <div className="rounded-lg border border-dashed p-4 text-center">
          <p className="mb-2 text-sm text-gray-500">
            No hay direcciones registradas
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAddressDialogOpen(true)}
            disabled={isSubmitting}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Agregar dirección
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Direcciones de entrega
          </h4>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {addresses.map((address) => {
              const isComplete = isAddressComplete(address);
              const missingFields = getMissingAddressFields(address);

              return (
                <div
                  key={address.id}
                  className={`relative cursor-pointer rounded-lg border p-3 transition-colors ${
                    selectedAddressId === address.id
                      ? "border-primary bg-primary/10"
                      : "hover:bg-gray-50"
                  } ${!isComplete ? "border-amber-200 bg-amber-50" : ""}`}
                  onClick={() => onSelect(address.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <p className="text-sm font-medium">
                          {address.street} {address.number}
                          {address.isDefault && (
                            <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">
                              Principal
                            </span>
                          )}
                        </p>
                        {!isComplete && (
                          <AlertTriangle className="ml-2 h-4 w-4 text-amber-500" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {address.comuna && `${address.comuna}, `}
                        {address.city}
                      </p>
                      {!isComplete && (
                        <div className="mt-1">
                          <p className="text-xs font-medium text-amber-600">
                            Dirección incompleta
                          </p>
                          <p className="text-xs text-amber-600">
                            Faltan: {missingFields.join(", ")}
                          </p>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleEditClick(address, e)}
                      className="h-8 w-8 p-0 hover:bg-gray-200"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-center rounded-lg border border-dashed p-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAddressDialogOpen(true)}
                disabled={isSubmitting}
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                Nueva dirección
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Address Dialog */}
      <AddressDialog
        isOpen={isAddressDialogOpen}
        onOpenChange={setIsAddressDialogOpen}
        title="Agregar nueva dirección"
        description="Ingrese los detalles de la nueva dirección de entrega."
        onSubmit={handleAddAddress}
        selectedRegion={selectedRegion}
        setSelectedRegion={setSelectedRegion}
        selectedComuna={selectedComuna}
        setSelectedComuna={setSelectedComuna}
        availableComunas={availableComunas}
        isEdit={false}
      />

      {/* Edit Address Dialog */}
      {editingAddress && (
        <AddressDialog
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          title="Editar dirección"
          description="Actualice los detalles de la dirección de entrega."
          onSubmit={handleEditAddress}
          defaultValues={{
            street: editingAddress.street,
            number: editingAddress.number,
            complement: editingAddress.complement || "",
            city: editingAddress.city,
            state:
              typeof editingAddress.state === "string"
                ? editingAddress.state
                : "",
            comuna:
              typeof editingAddress.comuna === "string"
                ? editingAddress.comuna
                : "",
            isDefault: editingAddress.isDefault,
          }}
          selectedRegion={selectedRegion}
          setSelectedRegion={setSelectedRegion}
          selectedComuna={selectedComuna}
          setSelectedComuna={setSelectedComuna}
          availableComunas={availableComunas}
          isEdit={true}
        />
      )}
    </div>
  );
};

export default AddressSelector;
